import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ValuationResults } from "@/pages/public/PropertyValuationPage";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Home } from "lucide-react";

interface ValuationResultsStepProps {
  valuation: ValuationResults;
  leadId: string | null;
  onNavigateToConsumer: () => void;
}

const ValuationResultsStep = ({ valuation, leadId, onNavigateToConsumer }: ValuationResultsStepProps) => {
  const [isInterested, setIsInterested] = useState(false);
  const [justInterestedInValue, setJustInterestedInValue] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle mutual exclusivity between checkboxes
  const handleInterestedChange = (checked: boolean) => {
    setIsInterested(checked);
    if (checked) setJustInterestedInValue(false);
  };

  const handleJustValueChange = (checked: boolean) => {
    setJustInterestedInValue(checked);
    if (checked) setIsInterested(false);
  };

  const handleSubmitInterest = async () => {
    if (!leadId) return;

    setIsSubmitting(true);
    try {
      const actionTaken = isInterested ? 'submit_interest' : 'just_value';
      
      // Edge function handles both lead update AND notification (secure via service role)
      const { error } = await supabase.functions.invoke(
        'send-valuation-interest-notification',
        {
          body: {
            lead_id: leadId,
            action_taken: actionTaken,
            interested_in_selling: isInterested,
            message: message,
            just_value: justInterestedInValue
          }
        }
      );

      if (error) {
        console.error('Failed to submit interest:', error);
        throw error;
      }

      toast.success("Thank you! We'll be in touch soon.");
      
      setTimeout(() => {
        onNavigateToConsumer();
      }, 2000);
    } catch (error) {
      console.error('Error submitting interest:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLearnMore = async () => {
    if (!leadId) {
      onNavigateToConsumer();
      return;
    }

    try {
      // Edge function handles both lead update AND notification (secure via service role)
      const { error } = await supabase.functions.invoke(
        'send-valuation-interest-notification',
        {
          body: {
            lead_id: leadId,
            action_taken: 'learn_more',
            interested_in_selling: false,
            message: null,
            just_value: false
          }
        }
      );

      if (error) {
        console.error('Learn more tracking failed (non-blocking):', error);
      }
    } catch (error) {
      console.error('Error in handleLearnMore:', error);
    }

    onNavigateToConsumer();
  };

  return (
    <div className="space-y-6">
      {/* Valuation Display */}
      <Card className="border-godyspo-gold/20">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl mb-2">Your Home's Estimated Value</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <div className="mb-6">
            <div className="text-5xl font-bold text-godyspo-gold mb-2">
              {valuation.formatted_range}
            </div>
            <div className="text-2xl text-muted-foreground mb-3">
              Estimated Value: <span className="text-white font-semibold">{valuation.formatted_value}</span>
            </div>
            <p className="text-muted-foreground">Based on current market data and property details</p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>Disclaimer:</strong> This estimate is based on automated valuation models and publicly 
              available data. It is not an appraisal or offer. Actual value may vary.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Interest Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5 text-godyspo-gold" />
            Interested in Selling? Let's Talk
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="interested"
                checked={isInterested}
                onCheckedChange={handleInterestedChange}
              />
              <Label htmlFor="interested" className="cursor-pointer leading-relaxed">
                Yes, I'm interested in selling my property
              </Label>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox 
                id="just-value"
                checked={justInterestedInValue}
                onCheckedChange={handleJustValueChange}
              />
              <Label htmlFor="just-value" className="cursor-pointer leading-relaxed text-muted-foreground">
                No I'm just interested in value
              </Label>
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="text-muted-foreground">
              Tell us more about your goals (optional)
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g., Looking to sell within 3 months, need quick cash offer, etc."
              className="mt-2 min-h-[100px]"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={handleSubmitInterest}
              disabled={isSubmitting}
              className="flex-1 bg-godyspo-gold hover:bg-godyspo-gold/90 text-godyspo-background font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Interest"
              )}
            </Button>
            <Button
              onClick={handleLearnMore}
              variant="outline"
              className="flex-1"
            >
              Learn More About Selling
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ValuationResultsStep;
